<script lang="ts">
import { computed, defineComponent, h, ref, shallowRef, unref, watch, withDirectives } from 'vue';

import { Touch } from '@components/directives';
import { CdsSlideViewGroupSymbol, useProvideSlideView } from '@components/components/CdsSlideView/context.ts';
import { CdsFunctionalButton } from '@components/components';
import { classNames } from '@components/utils';

import { provideTheme, useDirection, useDisabled, useGroup, useLocale, useRender } from '../../composable';

import { makeSlideViewProps } from './props';

import type { ITouchHandlers } from '@components/directives/touch';

export const componentName = 'CdsSlideView';
export default defineComponent({
  name: componentName,
  directives: { Touch },
  props: {
    ...makeSlideViewProps()
  },
  emits: {
    'update:modelValue': (value: any) => true
  },
  setup(props, { slots, expose }) {
    const group = useGroup(props, CdsSlideViewGroupSymbol);
    const { themeClasses } = provideTheme(props);
    const { disabledClasses } = useDisabled(props);
    const { directionClasses } = useDirection(props, componentName);
    const { slideView: slideViewLocale } = useLocale(props);

    const rootRef = ref<HTMLElement | undefined>();
    const isReversed = shallowRef(false);

    const transition = computed(() => {
      const axis = props.direction === 'vertical' ? 'y' : 'x';
      const direction = unref(isReversed) ? '-reverse' : '';

      return `cds-slide-view-${axis}${direction}-transition`;
    });

    const transitionCount = shallowRef(0);
    const transitionHeight = ref<undefined | string>(undefined);

    const activeIndex = computed(() => unref(group.items).findIndex((item) => unref(group.selected).includes(item.id)));

    watch(activeIndex, (newValue, oldValue) => {
      const itemsLength = unref(group.items).length;
      const lastIndex = itemsLength - 1;

      if (itemsLength <= 2) {
        isReversed.value = newValue < oldValue;
      } else if (newValue === lastIndex && oldValue === 0) {
        isReversed.value = true;
      } else if (newValue === 0 && oldValue === lastIndex) {
        isReversed.value = false;
      } else {
        isReversed.value = newValue < oldValue;
      }
    });

    useProvideSlideView({
      transition,
      rootRef,
      transitionCount,
      transitionHeight,
      isReversed
    });

    const canMoveBack = computed(() => props.continuous || unref(activeIndex) !== 0);
    const canMoveForward = computed(() => props.continuous || unref(activeIndex) !== unref(group.items).length - 1);

    function prev() {
      unref(canMoveBack) && group.prev();
    }

    function next() {
      unref(canMoveForward) && group.next();
    }

    const arrows = computed(() => {
      const arrows = [];

      const prevProps = {
        icon: props.prevIcon,
        class: 'cds-slide-view__arrow-left',
        onClick: group.prev,
        ariaLabel: slideViewLocale.prev
      };

      arrows.push(
        unref(canMoveBack)
          ? slots.prev
            ? slots.prev({ props: prevProps })
            : h(CdsFunctionalButton, {
                ...prevProps
              })
          : h('div')
      );

      const nextProps = {
        icon: props.nextIcon,
        class: 'cds-slide-view__arrow-right',
        onClick: group.next,
        ariaLabel: slideViewLocale.next
      };

      arrows.push(
        unref(canMoveForward)
          ? slots.next
            ? slots.next({ props: nextProps })
            : h(CdsFunctionalButton, { ...nextProps })
          : h('div')
      );

      return arrows;
    });

    const touchOptions = computed(() => {
      if (props.touch === false) return props.touch;

      const options: ITouchHandlers = {
        left: next,
        right: prev,
        start: ({ originalEvent }) => {
          originalEvent.stopPropagation();
        }
      };

      return {
        ...options,
        ...(props.touch === true ? {} : props.touch)
      };
    });

    useRender(() =>
      h(
        props.tag,
        {
          ref: rootRef,
          class: classNames('cds-slide-view', unref(directionClasses), unref(disabledClasses), unref(themeClasses), {
            'cds-slide-view--show-arrows-on-hover': props.showArrows === 'hover'
          })
        },
        withDirectives(
          h(
            'div',
            {
              class: 'cds-slide-view__container',
              style: { height: unref(transitionHeight) }
            },
            [
              slots.default?.({ group }),

              props.showArrows !== false && h('div', { class: 'cds-slide-view__controls' }, unref(arrows)),

              slots.additional?.({ group })
            ]
          ),
          [[Touch, unref(touchOptions)]]
        )
      )
    );

    expose({
      group,
      next,
      prev
    });

    return {};
  }
});
</script>

<template>Component: CdsSlideView</template>

<style lang="scss">
@import './CdsSlideView';
</style>
